import React, { FC, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { UserContext } from '@/contexts/UserContext';
import { useGetStreamKeys } from '@/adapters/stream-keys';
import { RunsheetRowRaw, useCreateRunsheet, useNormalizeRunsheet } from '@/adapters/runsheet-v2';
import { RUNSHEETS_URL } from '@/routePaths';
import RunsheetRowsTable, { EditableRunsheetRow, stripKeys, withKeys } from './RunsheetRowsTable';

const STEPS = ['Choose stream key', 'Upload CSV', 'Review & edit', 'Save'];

const RunsheetWizard: FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { userState } = useContext(UserContext);
  const { data: streamKeyList } = useGetStreamKeys(userState?.role);
  const streamKeys = useMemo(() => streamKeyList?.items ?? [], [streamKeyList?.items]);

  const [activeStep, setActiveStep] = useState(0);
  const [streamKey, setStreamKey] = useState('');
  const [title, setTitle] = useState('');
  const [csvText, setCsvText] = useState('');
  const [rawRows, setRawRows] = useState<RunsheetRowRaw[]>([]);
  const [normalizedRows, setNormalizedRows] = useState<EditableRunsheetRow[]>([]);

  const normalize = useNormalizeRunsheet();
  const createRunsheet = useCreateRunsheet();

  const handleFile = async (file: File): Promise<void> => {
    const text = await file.text();
    setCsvText(text);
    if (!title) {
      setTitle(file.name.replace(/\.csv$/i, ''));
    }
  };

  const handleNormalize = async (): Promise<void> => {
    if (!csvText.trim()) {
      enqueueSnackbar('Upload or paste a CSV first', { variant: 'warning' });
      return;
    }
    try {
      const result = await normalize.mutateAsync({ csvText });
      setRawRows(result.rows);
      setNormalizedRows(withKeys(result.normalizedRows));
      if (result.normalizedRows.length === 0) {
        enqueueSnackbar('AI returned no rows. Try editing the CSV and re-normalizing.', {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar(
          `AI extracted ${result.normalizedRows.length} row${
            result.normalizedRows.length === 1 ? '' : 's'
          }`,
          { variant: 'success' },
        );
        setActiveStep(2);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to normalize CSV', {
        variant: 'error',
      });
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!streamKey) {
      enqueueSnackbar('Pick a stream key first', { variant: 'warning' });
      return;
    }
    if (!title.trim()) {
      enqueueSnackbar('Give the runsheet a title', { variant: 'warning' });
      return;
    }
    const validRows = normalizedRows.filter((r) => r.title.trim().length > 0);
    if (validRows.length === 0) {
      enqueueSnackbar('At least one row with a title is required', {
        variant: 'warning',
      });
      return;
    }
    try {
      await createRunsheet.mutateAsync({
        streamKey,
        title: title.trim(),
        rows: rawRows,
        normalizedRows: stripKeys(validRows),
      });
      enqueueSnackbar('Runsheet created', { variant: 'success' });
      navigate(RUNSHEETS_URL);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to save runsheet', {
        variant: 'error',
      });
    }
  };

  const stepBody = (() => {
    switch (activeStep) {
      case 0:
        return (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Which stream key does this runsheet belong to?
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="streamkey-label">Stream key</InputLabel>
              <Select
                labelId="streamkey-label"
                label="Stream key"
                value={streamKey}
                onChange={(e) => setStreamKey(e.target.value)}
              >
                {streamKeys.map((sk) => (
                  <MenuItem key={sk.streamKey} value={sk.streamKey}>
                    {sk.name || sk.streamKey}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {streamKeys.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                You don&apos;t have any stream keys yet. Create one on the Stream Keys page first.
              </Alert>
            )}
          </Paper>
        );

      case 1:
        return (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upload your runsheet CSV
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Any column layout is fine — AI normalizes it to the canonical schema (title, speaker,
              suggested start/end, speaker email).
            </Typography>
            <Stack spacing={2}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ alignSelf: 'flex-start' }}
              >
                Choose CSV file
                <input
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleFile(file);
                  }}
                />
              </Button>
              <TextField
                label="Or paste CSV text"
                multiline
                minRows={8}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder="title,speaker,start,end&#10;Welcome,Ada Lovelace,09:00,09:10&#10;Keynote,Grace Hopper,09:10,09:40"
              />
            </Stack>
          </Paper>
        );

      case 2:
        return (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <TextField
                label="Runsheet title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
              />
              <Typography variant="body2" color="text.secondary">
                Review the AI-normalized rows and edit any that aren&apos;t quite right. These are
                what the matcher will see on stream start.
              </Typography>
              <RunsheetRowsTable rows={normalizedRows} onChange={setNormalizedRows} />
            </Stack>
          </Paper>
        );

      case 3:
        return (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Ready to save
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Stream key:</strong>{' '}
              {streamKeys.find((sk) => sk.streamKey === streamKey)?.name || streamKey}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Title:</strong> {title}
            </Typography>
            <Typography variant="body2">
              <strong>Rows:</strong> {normalizedRows.filter((r) => r.title.trim()).length}
            </Typography>
          </Paper>
        );

      default:
        return null;
    }
  })();

  const hasAtLeastOneValidRow = normalizedRows.some((r) => r.title.trim().length > 0);
  const canGoNext = (() => {
    if (activeStep === 0) return !!streamKey;
    if (activeStep === 1) return !!csvText.trim();
    if (activeStep === 2) return !!title.trim() && hasAtLeastOneValidRow;
    if (activeStep === 3) return !!streamKey && !!title.trim() && hasAtLeastOneValidRow;
    return false;
  })();

  const primaryLabel = (() => {
    if (activeStep === 1) {
      return normalize.isLoading ? 'Normalizing…' : 'Normalize with AI';
    }
    if (activeStep === 3) {
      return createRunsheet.isLoading ? 'Saving…' : 'Create runsheet';
    }
    return 'Next';
  })();

  const onPrimary = async (): Promise<void> => {
    if (activeStep === 1) {
      await handleNormalize();
      return;
    }
    if (activeStep === 3) {
      await handleSave();
      return;
    }
    setActiveStep((s) => s + 1);
  };

  const onBack = (): void => {
    if (activeStep === 0) {
      navigate(RUNSHEETS_URL);
      return;
    }
    setActiveStep((s) => s - 1);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(RUNSHEETS_URL)} size="small">
          Back to runsheets
        </Button>
      </Stack>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        New Runsheet
      </Typography>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {stepBody}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
        <Button onClick={onBack} disabled={normalize.isLoading || createRunsheet.isLoading}>
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </Button>
        <Button
          variant="contained"
          onClick={onPrimary}
          disabled={!canGoNext || normalize.isLoading || createRunsheet.isLoading}
          startIcon={
            activeStep === 1 ? (
              normalize.isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesomeIcon />
              )
            ) : activeStep === 3 && createRunsheet.isLoading ? (
              <CircularProgress size={16} />
            ) : undefined
          }
        >
          {primaryLabel}
        </Button>
      </Stack>
    </Box>
  );
};

export default RunsheetWizard;
